import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { USER_PLAN_SERVICE_METHOD, USER_SERVICES_METHOD } from "services/commonService";
import { apiRequest } from "services/ServiceUtil";
import { MyPlanDetail, MyPlanDetailState } from "./MyPlanDetail";

const SLICE = "myPlanDetails";

const initialState: MyPlanDetailState = {
  isViewMode: false,
  myPlanDetails: {},
  status: "idle",
  ad: [],
  owner: {},
  userDetail: [],
  isUserDetailLoading: true,
  isRedirectFromNotification: false,
  myPlanDetailShowLoader: false,
  userValidationCheckLoader: false,
  importExistingActionDialog: {
    selectedTab: 0,
    searchText: ""
  },
  addToMyPlanLoader: false
};
export const addToMyPlansAsync = createAsyncThunk(`${SLICE}/addToMyPlansAsync`, (data: {}, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.postPlans, data, thunkApi)
);

export const fetchMyPlanDetailAsync = createAsyncThunk(`${SLICE}/fetchMyPlanDetailAsync`, (data: any, thunkApi) => {
  const params = {
    includePropertyInfo: data?.includePropertyInfo || false
  };
  return apiRequest(USER_PLAN_SERVICE_METHOD.getPlans, {}, thunkApi, [data.id], params);
});

export const fetchAutoCompleteAsync = createAsyncThunk(`${SLICE}/fetchAutoCompleteAsync`, (data: any, thunkApi) => {
  const { innCode, ...rest } = data;
  const updatedData = {
    ...rest,
    ...(innCode && { innCodes: innCode })
  };
  return apiRequest(USER_SERVICES_METHOD.getAssociates, {}, thunkApi, [], updatedData);
});

export const createSectionAsync = createAsyncThunk(`${SLICE}/createSectionAsync`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.postPlans, data, thunkApi, [data.planId, "sections"])
);

export const markPlanCompleteAsync = createAsyncThunk(`${SLICE}/markPlanCompleteAsync`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.putplans, data.payload, thunkApi, [data.planId, "bulk-action"])
);

export const removeMemberBulkActionAsync = createAsyncThunk(
  `${SLICE}/removeMemberBulkActionAsync`,
  (data: any, thunkApi) =>
    apiRequest(USER_PLAN_SERVICE_METHOD.putplans, data.payload, thunkApi, [data?.planId, "bulk-action"])
);

export const editSectionAsync = createAsyncThunk(`${SLICE}/editSectionAsync`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.patchPlans, data, thunkApi, [data.planId, "sections", data.sectionId])
);

export const deleteSectionAsync = createAsyncThunk(`${SLICE}/deleteSectionAsync`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.deletePlans, {}, thunkApi, [data.planId, "sections", data.sectionId])
);
export const updateMyPlanCardDetailAsync = createAsyncThunk(
  `${SLICE}/updateMyPlanCardDetailAsync`,
  (data: any, thunkApi) => apiRequest(USER_PLAN_SERVICE_METHOD.patchPlans, data.payload, thunkApi, [data.url])
);

export const fetchUserRemoveValidationAsync = createAsyncThunk(
  `${SLICE}/fetchUserRemoveValidationAsync`,
  (data: any, thunkApi) =>
    apiRequest(USER_PLAN_SERVICE_METHOD.getPlans, {}, thunkApi, [data.planId, "validate-user-removal", data?.hltId])
);

export const fetchAssigneeDetailsAsync = createAsyncThunk(
  `${SLICE}/fetchAssigneeDetailsAsync`,
  (data: any, thunkApi) => {
    const params = { size: data.size, hltIds: data.value };
    return apiRequest(USER_SERVICES_METHOD.getAssociates, {}, thunkApi, [], params);
  }
);

export const patchReorderSectionsAsync = createAsyncThunk(`${SLICE}/patchReorderSectionsAsync`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.patchPlans, data.reorderedSections, thunkApi, [data.id])
);

export const patchAddInviteeAsync = createAsyncThunk(`${SLICE}/patchAddInviteeAsync`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.patchPlans, data.inviteeList, thunkApi, [data.id])
);

export const patchUpdateOwnershipAsync = createAsyncThunk(`${SLICE}/patchUpdateOwnershipAsync`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.patchPlans, data.ownersList, thunkApi, [data.id])
);

export const updatePlanAsync = createAsyncThunk(`${SLICE}/updatePlanAsync`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.patchPlans, data.payload, thunkApi, [data.url])
);

export const deleteActionCardAsync = createAsyncThunk(`${SLICE}/deleteActionCardAsync`, (data: any, thunkApi) => {
  const { planId, sectionId, cardId } = data;
  const cardIds = cardId.join(",");
  const params = { cardId: cardIds };
  return apiRequest(
    USER_PLAN_SERVICE_METHOD.deletePlans,
    {},
    thunkApi,
    [planId, "sections", sectionId, "cards"],
    params
  );
});

export const copyActionAsync = createAsyncThunk(`${SLICE}/copyActionAsync`, (data: any, thunkApi) => {
  const { planId, sectionId } = data.url;
  return apiRequest(USER_PLAN_SERVICE_METHOD.postPlans, data.payload, thunkApi, [
    planId,
    "sections",
    sectionId,
    "cards"
  ]);
});

export const MyPlanDetailSlice = createSlice({
  name: SLICE,
  initialState,
  reducers: {
    clearUserDetail: (state) => {
      state.userDetail = [];
      state.isUserDetailLoading = true;
    },
    setIsRedirectFromNotification: (state, action) => {
      state.isRedirectFromNotification = action.payload;
    },
    setImportExistingActionDialogDetails: (state, action) => {
      state.importExistingActionDialog = {
        ...state.importExistingActionDialog,
        ...action.payload
      };
    },
    clearMyPlanDetails: (state) => {
      state.myPlanDetails = {};
    },
    setIsViewMode: (state, action) => {
      state.isViewMode = action.payload;
    }
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchMyPlanDetailAsync.pending, (state) => {
        state.myPlanDetailShowLoader = true;
        state.status = "loading";
      })
      .addCase(fetchMyPlanDetailAsync.fulfilled, (state, action: PayloadAction<MyPlanDetail>) => {
        state.status = "idle";
        state.ad = [];
        state.myPlanDetails = action.payload;
        state.myPlanDetailShowLoader = false;
      })
      .addCase(fetchMyPlanDetailAsync.rejected, (state) => {
        state.status = "failed";
        state.myPlanDetailShowLoader = false;
      })
      .addCase(createSectionAsync.pending, (state) => {
        state.status = "loading";
        state.myPlanDetailShowLoader = true;
      })
      .addCase(createSectionAsync.fulfilled, (state, action: PayloadAction<MyPlanDetail>) => {
        state.status = "idle";
        state.myPlanDetailShowLoader = false;
      })
      .addCase(createSectionAsync.rejected, (state) => {
        state.status = "failed";
        state.myPlanDetailShowLoader = false;
      })
      .addCase(markPlanCompleteAsync.pending, (state, action) => {
        if (action?.meta?.arg?.type !== "bulk_assign") state.myPlanDetailShowLoader = true;
      })
      .addCase(markPlanCompleteAsync.fulfilled, (state, action: PayloadAction<MyPlanDetail>) => {
        state.myPlanDetailShowLoader = false;
        state.myPlanDetails = action.payload;
      })
      .addCase(markPlanCompleteAsync.rejected, (state) => {
        state.myPlanDetailShowLoader = false;
      })
      .addCase(editSectionAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editSectionAsync.fulfilled, (state, action: PayloadAction<MyPlanDetail>) => {
        state.status = "idle";
        state.myPlanDetails = action.payload;
      })
      .addCase(fetchAssigneeDetailsAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAssigneeDetailsAsync.fulfilled, (state, action) => {
        state.status = "idle";
        state.ad = action.payload?.users;
        if (state.myPlanDetails.owners?.length) {
          state.owner = state.myPlanDetails.owners?.map((item: any) =>
            action.payload?.users.find((user: any) => user.hltId === item)
          );
        }
        state.isUserDetailLoading = false;
      })
      .addCase(fetchAssigneeDetailsAsync.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(fetchUserRemoveValidationAsync.pending, (state) => {
        state.userValidationCheckLoader = true;
      })
      .addCase(fetchUserRemoveValidationAsync.fulfilled, (state, action) => {
        state.userValidationCheckLoader = false;
      })
      .addCase(fetchUserRemoveValidationAsync.rejected, (state, action) => {
        state.userValidationCheckLoader = false;
      })
      .addCase(removeMemberBulkActionAsync.pending, (state) => {
        state.userValidationCheckLoader = true;
      })
      .addCase(removeMemberBulkActionAsync.fulfilled, (state, action) => {
        state.myPlanDetails = action?.payload;
        state.userValidationCheckLoader = false;
      })
      .addCase(removeMemberBulkActionAsync.rejected, (state, action) => {
        state.userValidationCheckLoader = false;
      })
      .addCase(fetchAutoCompleteAsync.pending, (state, action) => {
        state.isUserDetailLoading = true;
      })
      .addCase(fetchAutoCompleteAsync.fulfilled, (state, action) => {
        state.status = "idle";
        state.isUserDetailLoading = false;
        state.userDetail = action?.payload?.users;
      })
      .addCase(fetchAutoCompleteAsync.rejected, (state, action) => {
        state.status = "idle";
        state.isUserDetailLoading = false;
        state.userDetail = [];
      })
      .addCase(patchReorderSectionsAsync.fulfilled, (state, action: PayloadAction<MyPlanDetail>) => {
        state.myPlanDetails = action.payload;
      })
      .addCase(patchAddInviteeAsync.fulfilled, (state, action: PayloadAction<MyPlanDetail>) => {
        state.myPlanDetails = action.payload;
      })

      .addCase(patchUpdateOwnershipAsync.fulfilled, (state, action: PayloadAction<MyPlanDetail>) => {
        state.myPlanDetails = action.payload;
      })
      .addCase(updatePlanAsync.fulfilled, (state, action: PayloadAction<MyPlanDetail>) => {
        state.myPlanDetails = action.payload;
      })
      .addCase(updateMyPlanCardDetailAsync.fulfilled, (state, action: any) => {
        state.myPlanDetails = action.payload.userPlanDetail;
      })
      .addCase(copyActionAsync.fulfilled, (state, action: any) => {
        state.myPlanDetails = action.payload.userPlanDetail;
      })
      .addCase(addToMyPlansAsync.pending, (state, action: any) => {
        state.addToMyPlanLoader = true;
      })
      .addCase(addToMyPlansAsync.fulfilled, (state, action: any) => {
        state.myPlanDetails = action.payload;
        state.addToMyPlanLoader = false;
      })
      .addCase(addToMyPlansAsync.rejected, (state, action: any) => {
        state.addToMyPlanLoader = false;
      });
  }
});

export const {
  setIsViewMode,
  clearUserDetail,
  setIsRedirectFromNotification,
  setImportExistingActionDialogDetails,
  clearMyPlanDetails
} = MyPlanDetailSlice.actions;

export default MyPlanDetailSlice.reducer;
